import React, { useState, useEffect } from "react";
import Masonry from "react-masonry-css";
import LazyImage from "./LazyImage";
import SelectedImagesStrip from "./SelectedImagesStrip";
import StackedImages from "./StackedImages";
import html2canvas from "html2canvas";

// Define the breakpoint columns for the Masonry layout
const breakpointColumnsObj = {
    default: 4,
    960: 3,
    568: 2,
    320: 1,
};

// Define the main component for the collage gallery app
const CollageGalleryApp = () => {
    // Define the state variables for the images, selected images, and whether to display the stacked images
    const [images, setImages] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [displayStackedImages, setDisplayStackedImages] = useState(false);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

    // Define a function to toggle the selection of an image
    const toggleImageSelection = (url) => {
        // Check if the image is already selected
        if (selectedImages.includes(url)) {
            // If the image is already selected, remove it from the selected images array
            setSelectedImages((prevSelectedImages) => prevSelectedImages.filter((img) => img !== url));
        } else {
            // If the image is not already selected, add it to the selected images array if there are less than 10 images selected
            if (selectedImages.length < 10) {
                setSelectedImages((prevSelectedImages) => [...prevSelectedImages, url]);
            }
        }
    };

    // Define a function to remove an image from the selected images strip
    const removeImageFromStrip = (index) => {
        setSelectedImages((prevSelectedImages) => prevSelectedImages.filter((_, i) => i !== index));
    };

    // Define a function to remove all images from the selected images strip
    const removeAllImagesFromStrip = () => {
        setSelectedImages([]);
    };

    // Define a function to handle the continue button click
    const handleContinueClick = () => {
        setDisplayStackedImages(true);
    };

    // Define a function to handle the "Remove All" button click
    const handleRemoveAllClick = () => {
        removeAllImagesFromStrip();
    };

    // Define a function to shuffle an array
    const shuffleArray = (array) => {
        // Loop through the array from the last element to the first element
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index between 0 and the current index
            const j = Math.floor(Math.random() * (i + 1));

            // Swap the current element with the element at the random index using destructuring assignment
            [array[i], array[j]] = [array[j], array[i]];
        }
    };

    // Fetch the images from the server on component mount
    useEffect(() => {
        // Define an async function to fetch the images
        const fetchData = async () => {
            try {
                // Fetch the image URLs from the server using the WordPress AJAX API
                const response = await fetch(`${ajax_object.siteAddress}/wp-admin/admin-ajax.php?action=collage_gallery_get_images`);

                // If the response is not OK, throw an error
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                // Parse the response body as JSON to get the image URLs
                const imageUrls = await response.json();

                // Shuffle the image URLs randomly using the shuffleArray function
                shuffleArray(imageUrls);

                // Set the state with the shuffled image URLs
                setImages(imageUrls);
            } catch (error) {
                // Log any errors that occur during the fetch operation
                console.error("Error fetching images:", error);
            }
        };

        // Call the fetchData function when the component mounts
        fetchData();
    }, []);

    // Define a function to download the collage image
    const downloadCollage = async () => {
        // Get the download button and its child elements
        let downloadButton = document.querySelector("#download-button");
        let downloadButtonText = downloadButton.querySelector(".button-text");
        let downloadButtonSpinner = downloadButton.querySelector(".spinner");

        // Hide the button text and show the spinner
        downloadButtonText.style.display = "none";
        downloadButtonSpinner.style.display = "block";

        // Get the screenshot container element
        const screenshotContainer = document.querySelector(".stacked-images__screenshot");

        // Use html2canvas to capture the screenshot and create a canvas element
        const canvas = await html2canvas(screenshotContainer, { useCORS: true });

        // Convert the canvas to a data URL and then to a Blob object
        const dataUrl = await canvas.toDataURL("image/jpeg");
        const data = atob(dataUrl.split(",")[1]);
        let array = [];
        for (let i = 0; i < data.length; i++) {
            array.push(data.charCodeAt(i));
        }
        let blob = new Blob([new Uint8Array(array)], { type: "image/jpeg" });

        // Create a link element with the Blob URL and click it to download the image
        let link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "collage-screenshot.jpg";
        link.click();

        // When the download is finished, hide the spinner and show the button text again
        downloadButtonText.style.display = "block";
        downloadButtonSpinner.style.display = "none";
    };

    // Define a function to capture the collage image and send it to the server
    const captureCollage = async () => {
        setSubmitButtonDisabled(true);
        // Get the submit button and its child elements
        let submitButton = document.querySelector("#submit-button");
        let submitButtonText = submitButton.querySelector(".button-text");
        let submitButtonSpinner = submitButton.querySelector(".spinner");

        // Hide the button text and show the spinner
        submitButtonText.style.display = "none";
        submitButtonSpinner.style.display = "block";

        // Get the screenshot container element
        const screenshotContainer = document.querySelector(".stacked-images__screenshot");

        // Use html2canvas to capture the screenshot and create a canvas element
        const canvas = await html2canvas(screenshotContainer, { useCORS: true });

        // Convert the canvas to a Blob object and create a FormData object to send to the server
        const formData = new FormData();
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg"));
        formData.append("file", blob);
        formData.append("action", "save_collage_screenshot");

        // Send the FormData object to the server using fetch
        return fetch(ajax_object.ajax_url, {
            method: "POST",
            body: formData,
        })
            .then((response) => {
                console.log("Fetch response:", response);
                return response.json();
            })
            .then((data) => {
                console.log("Fetch response data:", data);
                if (data.success) {
                    // Inform the user that the screenshot has been saved
                    alert("Your collage has been saved to the gallery!");
                } else {
                    console.error("Error saving collage screenshot:", data.message);
                }
            })
            .finally(() => {
                // When the request is finished, hide the spinner and show the button text again
                submitButtonText.style.display = "block";
                submitButtonSpinner.style.display = "none";
            });
    };

    // Render the component
    return (
        <>
            {!displayStackedImages ? (
                <>
                    <Masonry
                        breakpointCols={breakpointColumnsObj}
                        className="my-masonry-grid"
                        columnClassName="my-masonry-grid_column">
                        {images.map((url, index) => (
                            <LazyImage
                                key={index}
                                src={url}
                                alt={`Image ${index}`}
                                selected={selectedImages.includes(url)}
                                onClick={() => toggleImageSelection(url)}
                            />
                        ))}
                    </Masonry>
                    <SelectedImagesStrip
                        images={selectedImages}
                        onRemove={removeImageFromStrip}
                        onRemoveAll={handleRemoveAllClick}
                        minImages={5}
                        maxImages={10}
                        onContinue={handleContinueClick}
                    />
                </>
            ) : (
                <StackedImages images={selectedImages}>
                    <button
                        onClick={() => {
                            setDisplayStackedImages(false);
                            setSubmitButtonDisabled(false);
                        }}
                        className="stacked-images__button stacked-images__button--back">
                        Go Back
                    </button>
                    <button
                        onClick={downloadCollage}
                        className="stacked-images__button stacked-images__button--download"
                        id="download-button">
                        <span className="button-text">Download</span>
                        <span className="spinner"></span>
                    </button>
                    <button
                        disabled={submitButtonDisabled}
                        onClick={captureCollage}
                        className="stacked-images__button stacked-images__button--submit"
                        id="submit-button">
                        <span className="button-text">Submit</span>
                        <span className="spinner"></span>
                    </button>
                </StackedImages>
            )}
        </>
    );
};

export default CollageGalleryApp;
